'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import { SOFT_LOCKED_BLOCKED_PATHS } from '@/constants/subscription'
import { useFacebookLoginStatusQuery } from '@/hooks/query/facebook'
import { useUnreadNotificationCountQuery } from '@/hooks/query/notification'
import { setFacebookLoginStatus } from '@/stores/facebook.store'
import { useNotificationStore } from '@/stores/notification.store'
import { useSoftLockDialogStore } from '@/stores/softlock-dialog.store'
import { useUnSavedChangeStore } from '@/stores/unsaved-change-dialog.store'

import FeedbackDialog from '../feedback/feedback-dialog'
import LegalDialog from '../legal/legal-dialog'
import UserPreferencesDialog from '../user-preferences/user-preferences-dialog'

import { DashboardNav } from './dashboard-nav'

export const DashboardLayoutSidebar = ({
  canAccessInternalAnswerSearch = false,
}: {
  canAccessInternalAnswerSearch?: boolean
}) => {
  const router = useRouter()
  const { data: unreadCount = 0, isLoading, isFetching } = useUnreadNotificationCountQuery()
  const { data: isFacebookLoggedIn = false } = useFacebookLoginStatusQuery()

  const { hasUnsavedChanges, setOpenDialog } = useUnSavedChangeStore()
  const { hasSoftLock, setOpenDialogSoftLock } = useSoftLockDialogStore()
  const { setUnreadCount, setIsLoadingUnreadCount } = useNotificationStore()

  const [isOpenUseSetting, setIsOpenUseSetting] = useState(false)
  const [isOpenFeedback, setIsOpenFeedback] = useState(false)
  const [isOpenLegal, setIsOpenLegal] = useState(false)

  // Avoid updating state during render; update Facebook login status as a side-effect
  useEffect(() => {
    setFacebookLoginStatus(isFacebookLoggedIn)
  }, [isFacebookLoggedIn])

  useEffect(() => {
    // initialize unread notification count
    setUnreadCount(unreadCount)
  }, [unreadCount, setUnreadCount])

  useEffect(() => {
    // Sync loading state
    setIsLoadingUnreadCount(isLoading || isFetching)
  }, [isLoading, isFetching, setIsLoadingUnreadCount])

  const handleNavigationWithUnsavedChanges = (url?: string) => {
    if (!url) return
    if (hasUnsavedChanges) {
      setOpenDialog(true, url)
      return
    }
    const pathname = url.split('?')[0]

    if (hasSoftLock && SOFT_LOCKED_BLOCKED_PATHS.includes(pathname!)) {
      setOpenDialogSoftLock(true, url)
      return
    }
    router.push(url)
  }

  return (
    <>
      <DashboardNav
        setIsOpenUseSetting={setIsOpenUseSetting}
        setIsOpenFeedback={setIsOpenFeedback}
        setIsOpenLegal={setIsOpenLegal}
        onNavigate={handleNavigationWithUnsavedChanges}
        canAccessInternalAnswerSearch={canAccessInternalAnswerSearch}
      />
      <UserPreferencesDialog isOpen={isOpenUseSetting} onClose={() => setIsOpenUseSetting(false)} />
      <FeedbackDialog isOpen={isOpenFeedback} onClose={() => setIsOpenFeedback(false)} />
      <LegalDialog isOpen={isOpenLegal} onClose={() => setIsOpenLegal(false)} />
    </>
  )
}
